import { useEffect, useState } from "react";
import { Autocomplete, TextField, Box, Typography } from "@mui/material";
import { useApi } from "../../hooks/useApi.js";

export interface ComboOption {
  id: string;
  label: string;
  sub?: string;
}

// Ricerca su TUTTI i campi (label + sub) con ranking "inizia-con" prima del "contiene":
// chi inizia con il testo digitato sale in cima (es. "card" → Cardone), poi le parole che
// iniziano col testo, infine chi lo contiene in mezzo. A parità di rango si mantiene
// l'ordine originale (già ordinato per cognome/nome a monte).
function rankAndFilter(options: ComboOption[], input: string): ComboOption[] {
  const q = input.trim().toLowerCase();
  if (!q) return options;
  const text = (o: ComboOption) => `${o.label} ${o.sub ?? ""}`.toLowerCase();
  const rank = (o: ComboOption): number => {
    const label = o.label.toLowerCase();
    if (label.startsWith(q)) return 0; // l'etichetta inizia col testo
    if (text(o).split(/\s+/).some((w) => w.startsWith(q))) return 1; // una parola inizia col testo
    return 2; // contenuto in mezzo
  };
  return options
    .map((o, i) => ({ o, i, r: text(o).includes(q) ? rank(o) : -1 }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.o);
}

interface EntityComboProps<T> {
  label: string;
  path: string;
  mapItem: (item: T) => ComboOption;
  value: string;
  onChange: (id: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: "small" | "medium";
}

// Autocomplete generico che carica una lista da un endpoint (array o { data: [] }).
export function EntityCombo<T>({
  label,
  path,
  mapItem,
  value,
  onChange,
  required,
  error,
  helperText,
  disabled,
  size = "small"
}: EntityComboProps<T>) {
  const { call } = useApi();
  const [options, setOptions] = useState<ComboOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    call<{ data: T[] } | T[]>(path, { silent: true, noLoader: true })
      .then((r) => {
        if (!active) return;
        const list = Array.isArray(r) ? r : (r.data ?? []);
        setOptions(list.map(mapItem));
      })
      .catch(() => active && setOptions([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // mapItem è stabile (definito nei wrapper); ricarica solo al cambio di path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const selected = options.find((o) => o.id === value) ?? null;

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={selected}
      disabled={disabled}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOptions={(opts, state) => rankAndFilter(opts, state.inputValue)}
      onChange={(_, v) => onChange(v?.id ?? "")}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          <Box>
            <Typography variant="body2">{o.label}</Typography>
            {o.sub ? (
              <Typography variant="caption" color="text.secondary">{o.sub}</Typography>
            ) : null}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}
